import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/atoms/Button';
import { Input } from '../../components/atoms/Input';
import { Footer } from '../../components/organisms/Footer';
import { FortuneServiceCard } from '../../components/molecules/FortuneServiceCard';
import { tokens } from '../../design-system/tokens';
import { useAuth } from '../../contexts/AuthContext';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { getReferralCodeFromCookie } from '../../utils/referral';
import { getFirebaseErrorMessage } from '../../utils/firebaseError';

// Image assets from public folder
const imgBg1 = "/assets/images/hero-bg.png";
const imgImg = "/assets/images/fortune-today.png";
const img1 = "/assets/images/fortune-saju.png";
const imgFirefly11 = "/assets/images/fortune-compatibility.png";
const imgFirefly12 = "/assets/images/fortune-love.png";
const imgFirefly13 = "/assets/images/fortune-wealth.png";
const img2 = "/assets/images/fortune-tojung.png";

const Container = styled.div`
  background-color: ${tokens.colors.neutral[0]};
  position: relative;
  width: 100%;
  min-height: 100vh;
`;

const HeroSection = styled.div`
  position: relative;
  background: linear-gradient(135deg, #cccccc 0%, #e0e0e0 100%);
  height: 810px;
  border-bottom-left-radius: 50px;
  border-bottom-right-radius: 50px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: ${tokens.breakpoints.md}) {
    height: 500px;
    border-bottom-left-radius: 30px;
    border-bottom-right-radius: 30px;
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    height: 400px;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
  }
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  mix-blend-mode: multiply;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize['6xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  line-height: 1.2;
  color: ${tokens.colors.neutral[0]};
  text-align: center;
  letter-spacing: -0.05em;
  position: relative;
  z-index: 1;
  margin: 0;
  word-break: keep-all;
  padding: 0 ${tokens.spacing[4]};

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize['4xl']};
    padding: 0 ${tokens.spacing[6]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize['3xl']};
    padding: 0 ${tokens.spacing[5]};
  }
`;

const HeroSubtitle = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize['2xl']};
  font-weight: ${tokens.typography.fontWeight.medium};
  line-height: 1.6;
  color: ${tokens.colors.neutral[0]};
  text-align: center;
  letter-spacing: -0.05em;
  position: relative;
  z-index: 1;
  margin-top: ${tokens.spacing[5]};
  word-break: keep-all;
  padding: 0 ${tokens.spacing[4]};

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize.lg};
    margin-top: ${tokens.spacing[4]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.base};
    margin-top: ${tokens.spacing[3]};
  }
`;

const HeroButton = styled.button`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.lg};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[0]};
  background-color: ${tokens.colors.primary[500]};
  border: none;
  border-radius: ${tokens.borderRadius.full};
  padding: ${tokens.spacing[4]} ${tokens.spacing[16]};
  margin-top: ${tokens.spacing[10]};
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.ease};

  &:hover {
    background-color: ${tokens.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: ${tokens.shadows.lg};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing[3]} ${tokens.spacing[12]};
    font-size: ${tokens.typography.fontSize.base};
    margin-top: ${tokens.spacing[8]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[3]} ${tokens.spacing[10]};
    font-size: ${tokens.typography.fontSize.sm};
    margin-top: ${tokens.spacing[6]};
  }
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${tokens.spacing[24]} ${tokens.spacing[5]};

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing[16]} ${tokens.spacing[5]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[12]} ${tokens.spacing[4]};
  }
`;

const SectionTitle = styled.h2`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize['5xl']};
  font-weight: ${tokens.typography.fontWeight.semibold};
  line-height: 1.3;
  color: ${tokens.colors.neutral[800]};
  text-align: center;
  letter-spacing: -0.05em;
  margin-bottom: ${tokens.spacing[16]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize['3xl']};
    margin-bottom: ${tokens.spacing[12]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize['2xl']};
    margin-bottom: ${tokens.spacing[8]};
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-bottom: 150px;

  @media (max-width: ${tokens.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const LoginSection = styled.section`
  max-width: 400px;
  margin: 0 auto;
  padding: 50px 20px;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 30px;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 15px 0;
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${props => props.$active ? tokens.colors.primary[500] : '#aaaaaa'};
  background: none;
  border: none;
  border-bottom: 1px solid ${props => props.$active ? tokens.colors.primary[500] : '#dddddd'};
  cursor: pointer;
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.ease};

  &:hover {
    color: ${tokens.colors.primary[500]};
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LoginButton = styled(Button)`
  width: 250px;
  margin: 40px auto 20px;
`;

const SignupPrompt = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: ${tokens.typography.fontWeight.light};
  color: #777777;
  text-align: center;
  letter-spacing: -0.7px;
  margin-top: 30px;
`;

const SignupLink = styled.span`
  color: ${tokens.colors.primary[500]};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

const LegalNotice = styled.div`
  margin-top: ${tokens.spacing[4]};
  padding: ${tokens.spacing[3]} ${tokens.spacing[4]};
  background-color: ${tokens.colors.neutral[50]};
  border-radius: ${tokens.borderRadius.md};
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.xs};
  line-height: 1.6;
  color: ${tokens.colors.neutral[600]};
  text-align: center;

  a {
    color: ${tokens.colors.primary[500]};
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, #dddddd, transparent);
  margin: 100px 0;
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  font-size: 14px;
  text-align: center;
  margin-bottom: 20px;
  font-family: ${tokens.typography.fontFamily.primary};
`;

const SuccessMessage = styled.div`
  color: #44aa44;
  font-size: 14px;
  text-align: center;
  margin-bottom: 20px;
  font-family: ${tokens.typography.fontFamily.primary};
`;

const ForgotPasswordLink = styled.div`
  text-align: right;
  margin-top: 10px;
  margin-bottom: 10px;

  a {
    font-family: ${tokens.typography.fontFamily.primary};
    font-size: 13px;
    font-weight: ${tokens.typography.fontWeight.regular};
    color: #777777;
    cursor: pointer;
    text-decoration: none;
    letter-spacing: -0.65px;

    &:hover {
      color: ${tokens.colors.primary[500]};
      text-decoration: underline;
    }
  }
`;

// Fortune service data with Figma images (exact heights from Figma)
const fortuneServices = [
  {
    id: 'today',
    title: '오늘의 운세',
    description: '오늘은 어떤 기운이 따를까요?\n오늘 하루를 바꿀 작은 행운',
    image: imgImg,
    imageHeight: 110,
    nodeId: '141:105'
  },
  {
    id: 'saju',
    title: '사주팔자',
    description: '사주로 보는 내 삶의 설계도,\n태어난 순간이 말해주는 내 인생의 길',
    image: img1,
    imageHeight: 102,
    nodeId: '141:108'
  },
  {
    id: 'compatibility',
    title: '궁합',
    description: '우린 정말 잘 맞을까?\n궁합으로 알아보는 우리 사이',
    image: imgFirefly11,
    imageHeight: 95,
    nodeId: '141:111'
  },
  {
    id: 'love',
    title: '연애운',
    description: '두근거리는 만남의 시작,\n마음을 설레게 할 인연의 기운',
    image: imgFirefly12,
    imageHeight: 115,
    nodeId: '141:114'
  },
  {
    id: 'wealth',
    title: '재물운',
    description: '재물이 들어올 타이밍,\n부를 부르는 기운을 확인하세요',
    image: imgFirefly13,
    imageHeight: 95,
    nodeId: '141:117'
  },
  {
    id: 'tojung',
    title: '토정비결',
    description: '다가오는 한 해의 시작,\n럭키온 토정비결로 걱정 끝',
    image: img2,
    imageHeight: 110,
    nodeId: '141:120'
  }
];

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Scroll to login section
  const handleScrollToLogin = () => {
    const loginSection = document.getElementById('login-form');
    if (loginSection) {
      loginSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Signup form state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
  const [signupReferralCode, setSignupReferralCode] = useState('');

  // 쿠키에서 추천인 코드 자동 로드
  useEffect(() => {
    const referralCode = getReferralCodeFromCookie();
    if (referralCode && !signupReferralCode) {
      setSignupReferralCode(referralCode);
    }
  }, [signupReferralCode]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // AuthContext의 login 함수 사용 (Custom Token 방식)
      await login(loginEmail, loginPassword);

      // 로그인 성공 - 홈으로 이동
      navigate('/');
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || '로그인 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password match
    if (signupPassword !== signupPasswordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    // Validate password length
    if (signupPassword.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    setLoading(true);

    try {
      // AuthContext의 signup 함수 사용 (Custom Token 방식)
      const signupData: any = {
        email: signupEmail,
        password: signupPassword,
        displayName: signupName
      };

      // Only add referralCode if it's not empty
      if (signupReferralCode && signupReferralCode.trim() !== '') {
        signupData.referralCode = signupReferralCode.trim();
      }

      await signup(signupData);

      // 회원가입 성공 - 홈으로 이동
      navigate('/');
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message || '회원가입 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setSuccessMessage('');

    if (!loginEmail) {
      setError('비밀번호 재설정을 위해 이메일을 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, loginEmail);
      setSuccessMessage('비밀번호 재설정 이메일이 발송되었습니다. 이메일을 확인해주세요.');
    } catch (err: any) {
      const friendlyMessage = getFirebaseErrorMessage(err);
      setError(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: 'login' | 'signup') => {
    setActiveTab(tab);
    setError('');
    setSuccessMessage('');
  };

  return (
    <Container data-name="접속_로그인" data-node-id="85:96">
      <HeroSection data-name="Hero" data-node-id="85:130">
        <HeroBackground data-name="bg1" data-node-id="95:94">
          <img src={imgBg1} alt="Hero background" />
        </HeroBackground>
        <HeroTitle data-node-id="85:132">
          오늘 나에게 다가올 행운은?
        </HeroTitle>
        <HeroSubtitle data-node-id="85:133">
          당신의 행운이 빛나는 순간을 밝혀드립니다
        </HeroSubtitle>
        <HeroButton onClick={handleScrollToLogin}>
          시작하기
        </HeroButton>
      </HeroSection>

      <ContentSection>
        <SectionTitle data-node-id="85:162">
          당신을 빛내주는<br />
          숨겨진 행운의 메세지
        </SectionTitle>

        <ServiceGrid data-name="list" data-node-id="141:104">
          {fortuneServices.map((service) => (
            <FortuneServiceCard
              key={service.id}
              title={service.title}
              description={service.description}
              image={service.image}
              imageHeight={service.imageHeight}
              onClick={handleScrollToLogin}
              data-name={service.id}
            />
          ))}
        </ServiceGrid>

        <Divider data-name="Line" data-node-id="85:286" />

        <SectionTitle data-node-id="85:296">
          럭키온이 전하는 행운,<br />
          지금 확인해 보세요!
        </SectionTitle>

        <LoginSection id="login-form">
          <TabContainer data-name="tab" data-node-id="85:347">
            <Tab
              $active={activeTab === 'login'}
              onClick={() => handleTabChange('login')}
              data-name="로그인"
              data-node-id="85:329"
            >
              로그인
            </Tab>
            <Tab
              $active={activeTab === 'signup'}
              onClick={() => handleTabChange('signup')}
              data-name="회원가입"
              data-node-id="85:333"
            >
              회원가입
            </Tab>
          </TabContainer>

          {activeTab === 'login' ? (
            <>
              <FormContainer onSubmit={handleLoginSubmit}>
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
                <Input
                  type="email"
                  placeholder="이메일"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  disabled={loading}
                  data-name="input"
                  data-node-id="85:310"
                />
                <Input
                  type="password"
                  placeholder="비밀번호"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  disabled={loading}
                  data-name="input"
                  data-node-id="85:311"
                />
                <ForgotPasswordLink>
                  <a onClick={handleForgotPassword}>
                    비밀번호를 잊으셨나요?
                  </a>
                </ForgotPasswordLink>
                <LoginButton
                  type="submit"
                  variant="primary"
                  size="large"
                  disabled={loading}
                  data-name="btn1"
                  data-node-id="85:313"
                >
                  {loading ? '로그인 중...' : '로그인'}
                </LoginButton>
              </FormContainer>

              <SignupPrompt data-node-id="85:312">
                계정이 없으신가요? {' '}
                <SignupLink onClick={() => handleTabChange('signup')}>
                  회원가입
                </SignupLink>
              </SignupPrompt>
            </>
          ) : (
            <>
              <FormContainer onSubmit={handleSignupSubmit}>
                {error && <ErrorMessage>{error}</ErrorMessage>}
                <Input
                  type="text"
                  placeholder="이름"
                  value={signupName}
                  onChange={(e) => setSignupName(e.target.value)}
                  required
                  disabled={loading}
                  data-name="input"
                />
                <Input
                  type="email"
                  placeholder="이메일"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  required
                  disabled={loading}
                  data-name="input"
                />
                <Input
                  type="password"
                  placeholder="비밀번호"
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                  required
                  disabled={loading}
                  data-name="input"
                />
                <Input
                  type="password"
                  placeholder="비밀번호 확인"
                  value={signupPasswordConfirm}
                  onChange={(e) => setSignupPasswordConfirm(e.target.value)}
                  required
                  disabled={loading}
                  data-name="input"
                />
                <Input
                  type="text"
                  placeholder="추천인 코드 (선택)"
                  value={signupReferralCode}
                  onChange={(e) => setSignupReferralCode(e.target.value)}
                  disabled={loading}
                  data-name="input"
                />
                <LoginButton
                  type="submit"
                  variant="primary"
                  size="large"
                  disabled={loading}
                  data-name="btn1"
                >
                  {loading ? '가입 중...' : '가입하기'}
                </LoginButton>
              </FormContainer>

              <LegalNotice>
                회원가입 진행 시 <Link to="/terms">이용약관</Link>과 <Link to="/privacy">개인정보처리방침</Link>에 동의하시는 것으로 간주합니다.
              </LegalNotice>

              <SignupPrompt>
                이미 계정이 있으신가요? {' '}
                <SignupLink onClick={() => handleTabChange('login')}>
                  로그인
                </SignupLink>
              </SignupPrompt>
            </>
          )}
        </LoginSection>
      </ContentSection>

      <Footer data-name="Footer" data-node-id="106:87" />
    </Container>
  );
};